import React, { useEffect, useRef } from 'react';
import { css, Global } from '@emotion/react';

import {
  LearnerFinalSummaryReport,
  RecommendedResource,
} from 'redux/schemas/app/learner-final-summary';
import CountUp from './count-up';
import ViewToggle, { LearnerTakeawayView } from './view-toggle';
import { ltColors, ltFont, ltGlobalStyles } from './styles';
import { printTakeawayNode } from './print-takeaway';

type TakeawayTakeoverProps = {
  report: LearnerFinalSummaryReport;
  learnerFirstName: string;
  learnerName: string;
  learnerInitials: string;
  learnerProfilePicture: string;
  courseTitle: string;
  onClose: () => void;
  onOpenResource?: (resource: RecommendedResource) => void;
  view: LearnerTakeawayView;
  onSetView: (view: LearnerTakeawayView) => void;
};

const MAX_COURSE_HIGHLIGHT_SENTENCES = 2;
const MAX_COURSE_HIGHLIGHT_CHARS = 340;
const MAX_HERO_HEADLINE_CHARS = 170;

const rootStyles = css`
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: ${ltColors.navy};
  font-family: ${ltFont};

  @page {
    margin: 12mm;
  }

  .lt-scroll {
    height: 100%;
    overflow-y: auto;
    scroll-behavior: smooth;
  }

  .lt-topbar {
    position: sticky;
    top: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    background: ${ltColors.navy};
    border-bottom: 2px solid rgba(255, 255, 255, 0.12);
  }
  .lt-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${ltColors.white};
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  .lt-actions { display: flex; align-items: center; gap: 10px; }
  .lt-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: none;
    cursor: pointer;
    background: ${ltColors.pink};
    color: ${ltColors.white};
    font-family: ${ltFont};
    font-weight: 600;
    font-size: 14px;
    padding: 10px 20px;
    border-radius: 999px;
    animation: ltRing 2.4s ease-in-out infinite;
  }
  .lt-pill:hover { transform: scale(1.04); }
  .lt-close {
    border: none;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.12);
    color: ${ltColors.white};
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .lt-close:hover { background: rgba(255, 255, 255, 0.2); }

  .lt-sec {
    position: relative;
    min-height: 78vh;
    padding: 64px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .lt-inner { width: 100%; max-width: 640px; }
  .lt-center { text-align: center; }

  .lt-eyebrow {
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin: 0 0 12px;
  }
  .lt-disp { font-weight: 700; font-size: 46px; line-height: 0.98; margin: 0 0 16px; }
  .lt-h2 { font-weight: 700; font-size: 30px; line-height: 1.05; margin: 0 0 14px; }
  .lt-body { font-weight: 400; font-size: 18px; line-height: 1.55; margin: 0; }
  .lt-cap { font-weight: 500; font-size: 13px; letter-spacing: 0.05em; text-transform: uppercase; }

  .lt-hero-stack {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
  }
  .lt-avatar-cluster {
    position: relative;
    width: 148px;
    height: 148px;
    margin: 0 auto 4px;
  }
  .lt-avatar-shell {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 42px;
    border: 4px solid rgba(255, 255, 255, 0.92);
    box-shadow: 0 18px 42px rgba(0, 0, 0, 0.28);
    transform: rotate(-4deg);
    background: linear-gradient(140deg, ${ltColors.pink}, ${ltColors.periwinkle});
  }
  .lt-avatar-photo,
  .lt-avatar-fallback {
    width: 100%;
    height: 100%;
  }
  .lt-avatar-photo {
    object-fit: cover;
    display: block;
  }
  .lt-avatar-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${ltColors.white};
    font-size: 42px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }
  .lt-avatar-chip {
    position: absolute;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
    padding: 0 14px;
    border-radius: 999px;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  }
  .lt-avatar-chip--ai {
    right: -8px;
    top: 8px;
    background: ${ltColors.yellow};
    color: ${ltColors.navy};
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .lt-avatar-chip--badge {
    left: -12px;
    bottom: 12px;
    width: 52px;
    min-width: 52px;
    padding: 0;
    background: ${ltColors.white};
    color: ${ltColors.pink};
    font-size: 24px;
    transform: rotate(8deg);
  }
  .lt-confetti-dot {
    position: absolute;
    border-radius: 999px;
    opacity: 0.95;
  }
  .lt-confetti-dot--one {
    top: -10px;
    left: 18px;
    width: 16px;
    height: 16px;
    background: ${ltColors.green};
  }
  .lt-confetti-dot--two {
    right: 12px;
    bottom: -12px;
    width: 20px;
    height: 20px;
    background: ${ltColors.periwinkle};
  }
  .lt-confetti-dot--three {
    left: -6px;
    top: 46px;
    width: 10px;
    height: 10px;
    background: ${ltColors.yellow};
  }
  .lt-hero-note {
    max-width: 420px;
    margin: 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 15px;
    line-height: 1.45;
  }

  .lt-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 62px;
    border-radius: 20px;
    font-size: 30px;
    margin-bottom: 18px;
    box-shadow: 0 8px 0 rgba(0, 0, 0, 0.14);
  }

  .lt-bubble {
    position: relative;
    background: ${ltColors.white};
    border-radius: 36px;
    padding: 36px 32px;
    text-align: center;
  }

  .lt-ticket {
    position: relative;
    background: ${ltColors.white};
    border-radius: 20px;
    padding: 26px 28px 26px 34px;
    transform: rotate(-1.2deg);
  }
  .lt-ticket::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 14px;
    background: radial-gradient(circle at 0 9px, var(--lt-punch) 6px, transparent 7px) 0 0 / 14px 18px repeat-y;
  }
  .lt-ticket + .lt-ticket { margin-top: 16px; }

  .lt-list { margin: 12px 0 0; padding: 0; list-style: none; }
  .lt-list li {
    position: relative;
    padding-left: 22px;
    margin-bottom: 8px;
    font-size: 16px;
    line-height: 1.5;
    color: ${ltColors.navy};
  }
  .lt-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${ltColors.pink};
  }

  .lt-stats { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
  .lt-stat {
    flex: 1;
    min-width: 120px;
    background: rgba(255, 255, 255, 0.16);
    border-radius: 18px;
    padding: 18px;
    text-align: center;
  }
  .lt-bignum { display: block; font-weight: 700; font-size: 46px; line-height: 1; color: ${ltColors.yellow}; }
  .lt-statcap { font-weight: 500; font-size: 13px; letter-spacing: 0.05em; text-transform: uppercase; color: ${ltColors.white}; }

  .lt-res {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    cursor: pointer;
    border: none;
    padding: 14px;
    border-radius: 14px;
    background: #f3f1ff;
    margin-top: 10px;
  }
  .lt-res:hover { background: #e9e6ff; }
  .lt-resic {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: ${ltColors.periwinkle};
    color: ${ltColors.white};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 19px;
    flex: 0 0 42px;
  }
  .lt-resttl { display: block; font-weight: 600; font-size: 16px; color: ${ltColors.navy}; }
  .lt-rescap { font-size: 13px; color: #7a78a0; }

  .lt-tags { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px; }
  .lt-tag {
    font-weight: 500;
    font-size: 13px;
    padding: 7px 15px;
    border-radius: 999px;
    background: ${ltColors.navy};
    color: ${ltColors.white};
  }

  .lt-cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    cursor: pointer;
    background: ${ltColors.navy};
    color: ${ltColors.white};
    font-family: ${ltFont};
    font-weight: 600;
    font-size: 17px;
    padding: 14px 30px;
    border-radius: 999px;
  }
  .lt-footnote { margin-top: 26px; font-size: 12px; color: ${ltColors.navy}; opacity: 0.7; }

  .lt-float { display: inline-flex; animation: ltFloat 2.8s ease-in-out infinite; }
  .lt-pulse { display: inline-flex; animation: ltPulse 1.8s ease-in-out infinite; }
  .lt-spin { display: inline-flex; animation: ltSpin 7s linear infinite; }

  @media print {
    position: static;
    inset: auto;
    z-index: auto;
    background: #fff;
    color: #111827;

    .lt-topbar {
      display: none;
    }

    .lt-scroll {
      height: auto;
      overflow: visible;
    }

    .lt-sec {
      min-height: auto;
      padding: 18mm 0;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .lt-inner {
      max-width: none;
    }

    .lt-step {
      opacity: 1 !important;
      transform: none !important;
    }

    .lt-badge,
    .lt-avatar-chip,
    .lt-confetti-dot,
    .lt-float,
    .lt-pulse,
    .lt-spin {
      animation: none !important;
    }
  }
`;

/** Renders a bulleted list, omitting the block entirely when empty. */
const BulletList: React.FC<{ items: string[] }> = ({ items }) => {
  if (!items || items.length === 0) return null;
  return (
    <ul className='lt-list'>
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
};

const normalizeWhitespace = (text: string | null | undefined) => ` ${text || ''} `.replace(/\s+/g, ' ').trim();

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const trimAtWordBoundary = (text: string, maxChars: number) => {
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars - 3).trimEnd();
  const boundary = slice.lastIndexOf(' ');
  return `${(boundary > 0 ? slice.slice(0, boundary) : slice).trimEnd()}...`;
};

const splitIntoSentences = (text: string) => normalizeWhitespace(text)
  .split(/(?<=[.!?])\s+/)
  .map(sentence => sentence.trim())
  .filter(Boolean);

const compactCourseHighlights = (text: string) => {
  const normalized = normalizeWhitespace(text);
  if (!normalized) return '';
  const sentences = splitIntoSentences(normalized);
  if (sentences.length === 0) return trimAtWordBoundary(normalized, MAX_COURSE_HIGHLIGHT_CHARS);
  return trimAtWordBoundary(
    sentences.slice(0, MAX_COURSE_HIGHLIGHT_SENTENCES).join(' '),
    MAX_COURSE_HIGHLIGHT_CHARS,
  );
};

const rewriteLearnerFacingText = (
  text: string,
  learnerName: string,
  learnerFirstName: string,
  courseTitle: string,
) => {
  let rewritten = normalizeWhitespace(text);
  if (!rewritten) return '';

  const knownNames = [learnerName, learnerFirstName]
    .map(value => normalizeWhitespace(value))
    .filter(Boolean);

  knownNames.forEach((name) => {
    rewritten = rewritten.replace(new RegExp(`${escapeRegExp(name)}'s`, 'g'), 'your');
    rewritten = rewritten.replace(new RegExp(`\\b${escapeRegExp(name)}\\b`, 'g'), 'you');
  });

  if (courseTitle) {
    rewritten = rewritten.replace(
      new RegExp(`Throughout ${escapeRegExp(courseTitle)}, you\\b`, 'i'),
      'Throughout the course, you',
    );
  }

  rewritten = rewritten.replace(/^Throughout [^,]+, you\b/i, 'Throughout the course, you');

  rewritten = rewritten
    .replace(/\bHis\b/g, 'Your')
    .replace(/\bHer\b/g, 'Your')
    .replace(/\bTheir\b/g, 'Your')
    .replace(/\bhis\b/g, 'your')
    .replace(/\bher\b/g, 'your')
    .replace(/\btheir\b/g, 'your')
    .replace(/\bHe\b/g, 'You')
    .replace(/\bShe\b/g, 'You')
    .replace(/\bThey\b/g, 'You')
    .replace(/\bhe\b/g, 'you')
    .replace(/\bshe\b/g, 'you')
    .replace(/\bthey\b/g, 'you')
    .replace(/\bYou is\b/g, 'You are')
    .replace(/\byou is\b/g, 'you are')
    .replace(/\bYou was\b/g, 'You were')
    .replace(/\byou was\b/g, 'you were')
    .replace(/\bYou has\b/g, 'You have')
    .replace(/\byou has\b/g, 'you have')
    .replace(/\bYou engages\b/g, 'You engage')
    .replace(/\byou engages\b/g, 'you engage')
    .replace(/\bYou demonstrates\b/g, 'You demonstrate')
    .replace(/\byou demonstrates\b/g, 'you demonstrate')
    .replace(/\bYou prefers\b/g, 'You prefer')
    .replace(/\byou prefers\b/g, 'you prefer')
    .replace(/\byour strongest asset is your\b/gi, 'your strongest asset is')
    .replace(/\byour greatest strength is your\b/gi, 'your greatest strength is');

  return rewritten;
};

const lowercaseFirstCharacter = (text: string) => {
  if (!text) return '';
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
};

const buildHeroHeadline = (
  finalTakeaway: string,
  learnerName: string,
  learnerFirstName: string,
  courseTitle: string,
) => {
  const directTakeaway = rewriteLearnerFacingText(
    splitIntoSentences(finalTakeaway)[0] || finalTakeaway,
    learnerName,
    learnerFirstName,
    courseTitle,
  );
  const compactTakeaway = trimAtWordBoundary(directTakeaway, MAX_HERO_HEADLINE_CHARS);

  if (!compactTakeaway) {
    return learnerFirstName ? `${learnerFirstName}, your strongest asset is how you stayed with it.` : 'Your strongest asset is how you stayed with it.';
  }

  if (!learnerFirstName) return compactTakeaway;

  const headlineBody = /^(you|your)\b/i.test(compactTakeaway)
    ? lowercaseFirstCharacter(compactTakeaway)
    : compactTakeaway;

  return `${learnerFirstName}, ${headlineBody}`;
};

const TakeawayTakeover: React.FC<TakeawayTakeoverProps> = ({
  report,
  learnerFirstName,
  learnerName,
  learnerInitials,
  learnerProfilePicture,
  courseTitle,
  onClose,
  onOpenResource,
  view,
  onSetView,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('lt-in');
      });
    }, { root, threshold: 0.35 });
    root.querySelectorAll('.lt-step').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const ssc = report.stopStartContinue ?? { stop: [], start: [], continue: [] };
  const counts = report.evidenceCounts ?? { submissionsUsed: 0, rolePlaysUsed: 0, qnaItemsUsed: 0 };
  const recs = report.recommendations ?? { resources: [], topicsToExplore: [] };
  const heroHeadline = buildHeroHeadline(
    report.finalTakeaway,
    learnerName,
    learnerFirstName,
    courseTitle,
  );
  const courseHighlights = compactCourseHighlights(report.courseSummary);
  const learnerWorkSummary = rewriteLearnerFacingText(
    report.learnerWorkSummary,
    learnerName,
    learnerFirstName,
    courseTitle,
  );
  const learnerAvatarLabel = normalizeWhitespace(learnerFirstName || learnerName || 'Learner');
  const learnerBadge = normalizeWhitespace(learnerInitials || learnerFirstName.slice(0, 2) || 'AI').slice(0, 2).toUpperCase();

  const onPrint = () => {
    void printTakeawayNode(rootRef.current, `${courseTitle} | NovoEd`, 'lt-print-root');
  };

  return (
    <div css={rootStyles} ref={rootRef} role='dialog' aria-modal='true' aria-label='Your AI takeaway'>
      <Global styles={ltGlobalStyles} />
      <div className='lt-scroll' ref={scrollRef}>

        <div className='lt-topbar'>
          <span className='lt-brand'><i className='icon icon-badge lt-spin' aria-hidden='true' /> Your AI takeaway</span>
          <div className='lt-actions'>
            <ViewToggle value={view} onChange={onSetView} variant='dark' />
            <button type='button' className='lt-pill' onClick={onPrint}>
              <i className='icon icon-download' aria-hidden='true' /> Save as PDF
            </button>
            <button type='button' className='lt-close' onClick={onClose} aria-label='Close'>
              <i className='icon icon-close' aria-hidden='true' />
            </button>
          </div>
        </div>

        <section className='lt-sec lt-center' style={{ background: ltColors.navy }}>
          <div className='lt-inner'>
            <div className='lt-hero-stack'>
              <div className='lt-avatar-cluster' aria-hidden='true'>
                <div className='lt-avatar-shell'>
                  {learnerProfilePicture ? (
                    <img className='lt-avatar-photo' src={learnerProfilePicture} alt={`${learnerAvatarLabel} profile`} />
                  ) : (
                    <div className='lt-avatar-fallback'>{learnerBadge}</div>
                  )}
                </div>
                <span className='lt-avatar-chip lt-avatar-chip--ai'>AI</span>
                <span className='lt-avatar-chip lt-avatar-chip--badge'>
                  <i className='icon icon-badge' aria-hidden='true' />
                </span>
                <span className='lt-confetti-dot lt-confetti-dot--one' />
                <span className='lt-confetti-dot lt-confetti-dot--two' />
                <span className='lt-confetti-dot lt-confetti-dot--three' />
              </div>
              <p className='lt-eyebrow' style={{ color: ltColors.pink }}>{`• ${courseTitle} •`}</p>
              <h1 className='lt-disp' style={{ color: ltColors.pink, maxWidth: 620 }}>
                {heroHeadline}
              </h1>
              <p className='lt-hero-note'>Your personalized wrap-up is ready.</p>
            </div>
          </div>
        </section>

        <section className='lt-sec lt-step' style={{ background: ltColors.yellow }}>
          <div className='lt-inner'>
            <span className='lt-badge lt-float' style={{ background: ltColors.periwinkle, color: ltColors.white }}>
              <i className='icon icon-education' aria-hidden='true' />
            </span>
            <p className='lt-eyebrow' style={{ color: ltColors.pink }}>• the journey • 01</p>
            <h2 className='lt-h2' style={{ color: ltColors.periwinkle }}>Course Highlights</h2>
            <p className='lt-body' style={{ color: ltColors.navy }}>{courseHighlights}</p>
          </div>
        </section>

        <section className='lt-sec lt-step lt-center' style={{ background: ltColors.periwinkle }}>
          <div className='lt-inner'>
            <span className='lt-badge lt-pulse' style={{ background: ltColors.yellow, color: ltColors.navy }}>
              <i className='icon icon-dashboard' aria-hidden='true' />
            </span>
            <p className='lt-eyebrow' style={{ color: 'rgba(255,255,255,0.9)' }}>• by the numbers • 02</p>
            <h2 className='lt-h2' style={{ color: ltColors.white, marginBottom: 18 }}>You showed up!</h2>
            <div className='lt-stats'>
              <div className='lt-stat'><CountUp to={counts.submissionsUsed} className='lt-bignum' /><span className='lt-statcap'>submissions</span></div>
              <div className='lt-stat'><CountUp to={counts.rolePlaysUsed} className='lt-bignum' /><span className='lt-statcap'>role plays</span></div>
              <div className='lt-stat'><CountUp to={counts.qnaItemsUsed} className='lt-bignum' /><span className='lt-statcap'>discussions</span></div>
            </div>
          </div>
        </section>

        <section className='lt-sec lt-step' style={{ background: ltColors.pink }}>
          <div className='lt-inner'>
            <span className='lt-badge lt-float' style={{ background: ltColors.navy, color: ltColors.white }}>
              <i className='icon icon-work' aria-hidden='true' />
            </span>
            <p className='lt-eyebrow' style={{ color: 'rgba(255,255,255,0.9)' }}>• your work • 03</p>
            <div className='lt-ticket' style={{ '--lt-punch': ltColors.pink } as React.CSSProperties}>
              <p className='lt-body' style={{ color: ltColors.navy }}>{learnerWorkSummary}</p>
              {report.strengths?.length > 0 && (
                <>
                  <p className='lt-cap' style={{ color: ltColors.periwinkle, marginTop: 16 }}>What you do well</p>
                  <BulletList items={report.strengths} />
                </>
              )}
              {report.growthAreas?.length > 0 && (
                <>
                  <p className='lt-cap' style={{ color: ltColors.pink, marginTop: 16 }}>Room to grow</p>
                  <BulletList items={report.growthAreas} />
                </>
              )}
            </div>
          </div>
        </section>

        <section className='lt-sec lt-step' style={{ background: ltColors.navy }}>
          <div className='lt-inner'>
            <span className='lt-badge lt-spin' style={{ background: ltColors.pink, color: ltColors.white }}>
              <i className='icon icon-refresh' aria-hidden='true' />
            </span>
            <p className='lt-eyebrow' style={{ color: ltColors.pink }}>• stop · start · continue • 04</p>
            <div className='lt-ticket' style={{ '--lt-punch': ltColors.navy, borderTop: `6px solid ${ltColors.pink}` } as React.CSSProperties}>
              <p className='lt-cap' style={{ color: ltColors.pink }}>Stop</p>
              <BulletList items={ssc.stop} />
            </div>
            <div className='lt-ticket' style={{ '--lt-punch': ltColors.navy, borderTop: `6px solid ${ltColors.green}` } as React.CSSProperties}>
              <p className='lt-cap' style={{ color: ltColors.green }}>Start</p>
              <BulletList items={ssc.start} />
            </div>
            <div className='lt-ticket' style={{ '--lt-punch': ltColors.navy, borderTop: `6px solid ${ltColors.periwinkle}` } as React.CSSProperties}>
              <p className='lt-cap' style={{ color: ltColors.periwinkle }}>Continue</p>
              <BulletList items={ssc.continue} />
            </div>
          </div>
        </section>

        <section className='lt-sec lt-step' style={{ background: ltColors.mint }}>
          <div className='lt-inner'>
            <span className='lt-badge lt-float' style={{ background: ltColors.pink, color: ltColors.white }}>
              <i className='icon icon-ideas' aria-hidden='true' />
            </span>
            <p className='lt-eyebrow' style={{ color: ltColors.pink }}>• keep exploring • 05</p>
            <h2 className='lt-h2' style={{ color: ltColors.periwinkle, marginBottom: 14 }}>Where to go next</h2>
            {recs.resources?.map((resource) => (
              <button
                key={resource.reason}
                type='button'
                className='lt-res'
                onClick={() => onOpenResource?.(resource)}
              >
                <span className='lt-resic'><i className='icon icon-video' aria-hidden='true' /></span>
                <span style={{ flex: 1 }}>
                  <span className='lt-resttl'>{resource.title || 'Revisit this in your course'}</span>
                  <span className='lt-rescap'>{resource.reason}</span>
                </span>
                <i className='icon icon-arrow-right' aria-hidden='true' style={{ color: ltColors.periwinkle }} />
              </button>
            ))}
            {recs.topicsToExplore?.length > 0 && (
              <div className='lt-tags'>
                {recs.topicsToExplore.map((topic) => <span key={topic} className='lt-tag'>{topic}</span>)}
              </div>
            )}
          </div>
        </section>

        <section className='lt-sec lt-step lt-center' style={{ background: ltColors.yellow }}>
          <div className='lt-inner'>
            <span className='lt-badge lt-pulse' style={{ background: ltColors.pink, color: ltColors.white }}>
              <i className='icon icon-download' aria-hidden='true' />
            </span>
            <h2 className='lt-disp' style={{ color: ltColors.pink, fontSize: 40 }}>Take it with you!</h2>
            <p className='lt-body' style={{ color: ltColors.navy, maxWidth: 320, margin: '0 auto 20px' }}>
              Open it anytime from course home.
            </p>
            <button type='button' className='lt-cta' onClick={onPrint}>
              <i className='icon icon-download' aria-hidden='true' /> Save as PDF
            </button>
            {report.confidenceNotes?.length > 0 && (
              <p className='lt-footnote'>{report.confidenceNotes.join(' ')}</p>
            )}
          </div>
        </section>

      </div>
    </div>
  );
};

export default TakeawayTakeover;
